/**
 * Mutation dispatcher — the ONLY place gateway-mutating API calls happen
 * from the AI flow.
 *
 * Two callers reach it (BETA-28, AGENT-DESIGN section 3):
 *   - the popover, after a user clicks Approve on a `request_confirmation`
 *     card, for a kind in `GATED_KINDS`;
 *   - `dispatchUngated`, straight from the `request_confirmation` tool, for
 *     every other kind. Battex engineers own this plant; a card in front of
 *     an alarm acknowledgement was friction, not safety.
 *
 * `dispatchUngated` refuses a gated kind outright, so the direct path can
 * never be used to skip a card.
 *
 * Every mutation kind maps to one helper in api.js. Adding a new kind is the
 * 6-place checklist in docs/battex/04-ai-assistant.md.
 */

import * as api from "../api";
// Cyclic by construction: tools/confirm.js owns the gated list and calls
// dispatchUngated. Both sides dereference only at call time, so neither
// module needs the other during evaluation.
// eslint-disable-next-line import/no-cycle
import { GATED_KINDS } from "./tools/confirm";
import {
  buildEdgeModbusConnectorSpec,
  edgeConnectorTagEligibility,
} from "../pages/gateway/sections/edge-connector-spec";
import { applyHistorianToTagPaths } from "../pages/gateway/sections/tag-historian";
import { listTagsByPrefix } from "./analytics/tagSearch";

// Maps mutation.kind → resource tag broadcast on `gw-ai-resource-changed`
// after a successful run. Section pages listen for the resources they own
// and call their existing refresh function. Without this the AI's mutation
// hits the API but the page UI keeps showing stale rows until manual refresh.
const RESOURCE_BY_KIND = {
  delete_datasource: "datasources",
  delete_api_profile: "api_profiles",
  delete_credential: "credentials",
  delete_oauth_app: "oauth_apps",
  delete_mqtt_broker: "mqtt_brokers",
  delete_mqtt_subscription: "mqtt_subscriptions",
  delete_historian_provider: "historian_providers",
  delete_user: "users",
  update_user_role: "users",
  create_invite: "invites",
  revoke_invite: "invites",
  delete_group: "groups",
  add_group_members: "groups",
  remove_group_members: "groups",
  delete_project: "projects",
  delete_ai_provider: "ai_providers",
  set_default_ai_provider: "ai_providers",
  set_tags_historian: "tags",
  update_workspace_settings: "workspace_settings",
  update_my_profile: "my_profile",
  acknowledge_alarm: "live_alarms",
  delete_alarm: "alarm_rules",
  update_alarm: "alarm_rules",
  preview_edge_connector: "edge_connectors",
  preview_edge_modbus_client: "edge_connectors",
  apply_device_template: "edge_connectors",
  confirm_edge_connector: "edge_connectors",
  retry_edge_connector: "edge_connectors",
  preview_edge_connector_lifecycle: "edge_connectors",
  preview_edge_connector_rollback: "edge_connectors",
};

/**
 * @param {Object} mutation     {kind, ...args}
 * @returns {Promise<{ok: boolean, summary: string, error?: string}>}
 */
export async function dispatch(mutation) {
  if (!mutation || typeof mutation !== "object" || !mutation.kind) {
    return { ok: false, summary: "", error: "Invalid mutation payload." };
  }
  try {
    const summary = await runKind(mutation);
    const resource = RESOURCE_BY_KIND[mutation.kind];
    if (resource && typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("gw-ai-resource-changed", {
          detail: { resource, kind: mutation.kind },
        })
      );
    }
    return { ok: true, summary };
  } catch (err) {
    return {
      ok: false,
      summary: "",
      error: String(err?.message || err) || "Action failed.",
    };
  }
}

/**
 * The ungated path: run the mutation now, with no approval card.
 *
 * Refuses a gated kind rather than trusting the caller, so the only way to
 * reach a signed-edge or user-administration mutation stays the registered
 * pending action the human approved.
 *
 * @param {Object} mutation     {kind, ...args}
 * @returns {Promise<{ok: boolean, summary: string, error?: string}>}
 */
export async function dispatchUngated(mutation) {
  if (mutation && typeof mutation === "object" && GATED_KINDS.has(mutation.kind)) {
    return {
      ok: false,
      summary: "",
      error: `Mutation "${mutation.kind}" needs explicit user approval — raise a confirmation card instead of running it directly.`,
    };
  }
  return await dispatch(mutation);
}

async function runKind(m) {
  switch (m.kind) {
    // ── Form submission ────────────────────────────────────────────────
    case "submit_open_form":
      return await submitOpenForm();

    // ── Datasources ────────────────────────────────────────────────────
    case "delete_datasource":
      requireString(m, "name");
      await api.deleteDatasource(m.name);
      return `Deleted datasource "${m.name}".`;

    // ── API profiles ───────────────────────────────────────────────────
    case "delete_api_profile":
      requireString(m, "id");
      await api.deleteApiProfile(m.id);
      return `Deleted API profile.`;

    // ── Credentials ────────────────────────────────────────────────────
    case "delete_credential":
      requireString(m, "id");
      await api.deleteCredential(m.id);
      return `Deleted credential.`;

    // ── OAuth apps ─────────────────────────────────────────────────────
    case "delete_oauth_app":
      requireString(m, "provider");
      await api.deleteOAuthApp(m.provider);
      return `Removed ${m.provider} OAuth app config.`;

    // ── MQTT brokers / subscriptions ───────────────────────────────────
    case "delete_mqtt_broker":
      requireString(m, "id");
      await api.deleteMqttBroker(m.id);
      return `Deleted MQTT broker.`;
    case "delete_mqtt_subscription":
      requireString(m, "id");
      await api.deleteMqttSubscription(m.id);
      return `Deleted MQTT subscription.`;

    // ── Historian providers ────────────────────────────────────────────
    case "delete_historian_provider":
      requireString(m, "id");
      await api.deleteHistorianProvider(m.id);
      return `Deleted historian provider.`;

    // ── Users / invites / roles ────────────────────────────────────────
    case "delete_user":
      requireString(m, "id");
      await api.deleteUser(m.id);
      return `Deleted user.`;
    case "update_user_role":
      requireString(m, "id");
      requireRole(m);
      await api.updateUserRole(m.id, m.role);
      return `Updated user role to "${m.role}".`;
    case "create_invite": {
      requireString(m, "email");
      requireRole(m);
      const res = await api.createInvite({ email: m.email, role: m.role });
      // Backend returns { invite_url, email_sent }. The invite_url is a
      // shareable token-bearing path the user can hand to the invitee
      // directly — it's NOT just for the email. Surface it so the AI can
      // give the link to the user (a previous version claimed no link
      // existed, which was wrong).
      const path = res?.invite_url || "";
      const fullUrl =
        path && typeof window !== "undefined" && window.location?.origin
          ? `${window.location.origin}${path}`
          : path;
      const emailNote = res?.email_sent
        ? `An invite email was sent to ${m.email}.`
        : `An invite was created for ${m.email} (no email was sent — share the link directly).`;
      return fullUrl
        ? `${emailNote} Shareable invite link: ${fullUrl}`
        : emailNote;
    }
    case "revoke_invite":
      requireString(m, "id");
      await api.revokeInvite(m.id);
      return `Revoked invite.`;

    // ── Groups ─────────────────────────────────────────────────────────
    case "delete_group":
      requireString(m, "id");
      await api.deleteGroup(m.id);
      return `Deleted group.`;
    case "add_group_members": {
      requireString(m, "id");
      requireArray(m, "userIds");
      const updated = await api.addGroupMembers(m.id, m.userIds);
      const total = Array.isArray(updated?.userIds) ? updated.userIds.length : null;
      const groupName = updated?.name ? `"${updated.name}"` : "the group";
      return total != null
        ? `Added ${m.userIds.length} member(s) to ${groupName}; group now has ${total} member(s).`
        : `Added ${m.userIds.length} member(s) to ${groupName}.`;
    }
    case "remove_group_members": {
      requireString(m, "id");
      requireArray(m, "userIds");
      const updated = await api.removeGroupMembers(m.id, m.userIds);
      const total = Array.isArray(updated?.userIds) ? updated.userIds.length : null;
      const groupName = updated?.name ? `"${updated.name}"` : "the group";
      return total != null
        ? `Removed ${m.userIds.length} member(s) from ${groupName}; group now has ${total} member(s).`
        : `Removed ${m.userIds.length} member(s) from ${groupName}.`;
    }

    // ── Projects ───────────────────────────────────────────────────────
    case "delete_project":
      requireString(m, "id");
      await api.deleteProject(m.id);
      return `Deleted project.`;

    // ── AI providers ───────────────────────────────────────────────────
    case "delete_ai_provider":
      requireString(m, "id");
      await api.deleteAiProvider(m.id);
      return `Deleted AI provider.`;
    case "set_default_ai_provider":
      requireString(m, "id");
      await api.setDefaultAiProvider(m.id);
      return `Set default AI provider.`;

    // ── Alarms ─────────────────────────────────────────────────────────
    // The backend's canonical id field is `alarm_id`, but the AI commonly
    // emits `id` from prior tool calls or training data. Accept either.
    case "acknowledge_alarm": {
      const alarmId = pickAlarmId(m);
      await api.acknowledgeAlarm(alarmId);
      return `Acknowledged alarm.`;
    }
    case "delete_alarm": {
      const alarmId = pickAlarmId(m);
      await api.deleteAlarm(alarmId);
      return `Deleted alarm rule.`;
    }
    case "update_alarm": {
      const alarmId = pickAlarmId(m);
      const allowed = [
        "name",
        "mode",
        "setpoint",
        "setpoint_high",
        "severity",
        "message",
        "on_delay_ms",
        "off_delay_ms",
        "enabled",
        "notify_all",
        "notify_user_ids",
        "notify_group_ids",
      ];
      const VALID_MODES = new Set(["above", "below", "between", "outside_range"]);
      const VALID_SEVERITIES = new Set(["critical", "high", "medium", "low"]);
      const patch = {};
      for (const key of allowed) {
        if (m[key] !== undefined) patch[key] = m[key];
      }
      if (patch.mode != null && !VALID_MODES.has(patch.mode)) {
        throw new Error(
          `mode must be one of: ${[...VALID_MODES].join(", ")}.`
        );
      }
      if (patch.severity != null && !VALID_SEVERITIES.has(patch.severity)) {
        throw new Error(
          `severity must be one of: ${[...VALID_SEVERITIES].join(", ")}.`
        );
      }
      if (Object.keys(patch).length === 0) {
        throw new Error(
          "update_alarm needs at least one field to change (name, mode, setpoint, severity, enabled, etc.)."
        );
      }
      // Backend PUT /api/alarms/{id} requires name/mode/setpoint/message in the
      // request body unconditionally (it 400s otherwise — line 258 of
      // core/alarmserver/handlers.go). We fetch the existing alarm and merge
      // the patch over it, so callers can pass partial updates like
      // `{enabled:false}` without 400ing.
      const existingResp = await api.listAlarms();
      const list = Array.isArray(existingResp)
        ? existingResp
        : existingResp?.alarms || [];
      const existing = list.find(
        (a) => String(a.alarm_id) === String(alarmId)
      );
      if (!existing) {
        throw new Error(`No alarm rule found with alarm_id "${alarmId}".`);
      }
      const merged = { ...existing, ...patch };
      // Backend rejects setpoint_high when the effective mode is above/below
      // (line 271-273 of core/alarmserver/handlers.go). If a between-mode
      // alarm carries a stale setpoint_high and the patch flips mode to
      // above/below, the merged body would 400. Drop it cleanly.
      const effectiveMode = patch.mode || existing.mode;
      if (effectiveMode === "above" || effectiveMode === "below") {
        delete merged.setpoint_high;
      }
      await api.updateAlarm(alarmId, merged);
      const parts = Object.entries(patch).map(
        ([k, v]) => `${k} = ${typeof v === "string" ? `"${v}"` : v}`
      );
      return `Updated alarm rule: ${parts.join(", ")}.`;
    }

    // ── Account profile (the Account Settings page) ───────────────────
    case "update_my_profile": {
      const allowed = [
        "first_name",
        "last_name",
        "email",
        "phone",
        "location",
        "department",
        "avatar",
      ];
      const payload = {};
      for (const key of allowed) {
        if (m[key] != null) {
          if (typeof m[key] !== "string") {
            throw new Error(`"${key}" must be a string.`);
          }
          payload[key] = m[key];
        }
      }
      // Security boundary — passwords never go through the AI flow. Block
      // even if the model tries to sneak them in via a profile mutation.
      if (m.password != null || m.new_password != null || m.current_password != null) {
        throw new Error(
          "Password changes are not handled by the AI. Direct the user to Account Settings → Password card."
        );
      }
      if (Object.keys(payload).length === 0) {
        throw new Error(
          'update_my_profile needs at least one field: first_name, last_name, email, phone, location, department, or avatar.'
        );
      }
      await api.updateProfile(payload);
      // Keep the topbar avatar/name in sync — same event the page dispatches
      // after its own save.
      if (typeof window !== "undefined") {
        try {
          window.dispatchEvent(new CustomEvent("gw-profile-updated"));
        } catch {}
      }
      const parts = Object.entries(payload).map(([k, v]) => `${k} = "${v}"`);
      return `Updated your profile: ${parts.join(", ")}.`;
    }

    // ── Workspace settings (the Gateway Settings page) ─────────────────
    case "update_workspace_settings": {
      const payload = {};
      if (m.homepageRedirect != null) {
        if (typeof m.homepageRedirect !== "string") {
          throw new Error('"homepageRedirect" must be a string path (e.g. "/editor").');
        }
        payload.homepageRedirect = m.homepageRedirect;
      }
      if (m.runtimeAccess != null) {
        if (m.runtimeAccess !== "public" && m.runtimeAccess !== "private") {
          throw new Error('"runtimeAccess" must be "public" or "private".');
        }
        payload.runtimeAccess = m.runtimeAccess;
      }
      if (Object.keys(payload).length === 0) {
        throw new Error(
          '"update_workspace_settings" needs at least one field: homepageRedirect or runtimeAccess.'
        );
      }
      await api.updateWorkspaceSettings(payload);
      const parts = [];
      if (payload.homepageRedirect != null)
        parts.push(`Homepage Redirect = "${payload.homepageRedirect}"`);
      if (payload.runtimeAccess != null)
        parts.push(`Runtime Access = ${payload.runtimeAccess}`);
      return `Updated Gateway Settings: ${parts.join(", ")}.`;
    }

    // ── Signed edge connector desired state ───────────────────────────
    // Preview allocates an immutable revision but never touches the edge.
    // Applying it always requires a second, digest-bound confirmation.
    case "preview_edge_modbus_client": {
      // AI-assisted full Modbus CLIENT setup: the assistant supplies the
      // device settings and the COMPLETE points array; the same wizard
      // builder used by the human form validates and canonicalizes the
      // signed spec, then the ordinary preview/confirm review applies.
      requireString(m, "gateway_id");
      requireObject(m, "device");
      const clientSettings = m.device.settings && typeof m.device.settings === "object" ? m.device.settings : {};
      let clientPoints = Array.isArray(m.points) && m.points.length
        ? modbusPointsFromArray(m.points, m.tag_folder, clientSettings)
        : null;
      if (!clientPoints && typeof m.points_csv === "string" && m.points_csv.trim()) {
        clientPoints = modbusPointsFromCsv(m.points_csv, m.tag_folder, clientSettings);
      }
      if (!Array.isArray(clientPoints) || clientPoints.length === 0 || clientPoints.length > 200) {
        throw new Error("Provide the device's complete points: a points array or points_csv rows (1-200).");
      }
      // Normalize the many reasonable spellings of the transport kind; a
      // model cannot guess our exact enum, and the error must teach it.
      const kindAliases = {
        "modbus-tcp": "modbus-tcp",
        "modbus_tcp": "modbus-tcp",
        "modbustcp": "modbus-tcp",
        "modbus tcp": "modbus-tcp",
        "modbus-tcp-client": "modbus-tcp",
        "modbus tcp client": "modbus-tcp",
        "tcp": "modbus-tcp",
        "modbus-rtu": "modbus-rtu",
        "modbus_rtu": "modbus-rtu",
        "modbus rtu": "modbus-rtu",
        "modbus-rtu-client": "modbus-rtu",
        "rtu": "modbus-rtu",
        "modbus-rtu-over-tcp": "modbus-rtu-over-tcp",
        "modbus_rtu_over_tcp": "modbus-rtu-over-tcp",
        "modbus rtu over tcp": "modbus-rtu-over-tcp",
        "rtu-over-tcp": "modbus-rtu-over-tcp",
        "rtu_over_tcp": "modbus-rtu-over-tcp",
      };
      const clientKind = kindAliases[String(m.device.kind || "modbus-tcp").trim().toLowerCase()];
      if (!clientKind) {
        throw new Error('device.kind must be "modbus-tcp", "modbus-rtu", or "modbus-rtu-over-tcp" (got "' + String(m.device.kind) + '").');
      }
      const clientConnectorID = String(m.device.id || "").trim();
      if (clientConnectorID && (!Number.isSafeInteger(m.expected_revision) || m.expected_revision < 0)) {
        throw new Error("Editing a connection requires its current expected_revision (a whole number) from the runtime inventory — read it with get_edge_connectors.");
      }
      const clientSpec = buildEdgeModbusConnectorSpec({
        kind: clientKind,
        name: m.device.name,
        connectorID: clientConnectorID,
        desiredState: m.device.desired_state || "running",
        settings: clientSettings,
        points: clientPoints,
      });
      const client = await previewEdgeModbusClient(m.gateway_id, clientSpec, m.expected_revision);
      return [...client.notes, connectorPreviewSummary(client.preview, m.gateway_id)].join(" ");
    }
    case "apply_device_template": {
      // The one mutation that goes all the way to a running connection under a
      // single approval. It is allowed to, and preview_edge_modbus_client is
      // not, because of what the human is actually approving in each case:
      //
      //  - here, a register map that was validated when the template was saved,
      //    applied to ONE device whose host, unit id and name are on the card.
      //    Nothing about the points is authored in this turn, so a second
      //    review of them would be a review of a document the model did not
      //    write.
      //  - there, the model authored every point from a datasheet in this turn.
      //    The preview is exactly the artifact that has to be read before it is
      //    queued, so it stays two approvals.
      //
      // See docs/battex/04-ai-assistant.md.
      requireString(m, "gateway_id");
      requireString(m, "template");
      requireString(m, "name");
      if (!Number.isSafeInteger(m.unit_id) || m.unit_id < 0 || m.unit_id > 255) {
        throw new Error('"unit_id" must be the device address as a whole number (0-255 on Modbus TCP, 1-247 on serial).');
      }
      const instance = {
        gateway_id: m.gateway_id,
        name: m.name,
        unit_id: m.unit_id,
        ...(typeof m.host === "string" && m.host.trim() ? { host: m.host.trim() } : {}),
        ...(typeof m.serial_port === "string" && m.serial_port.trim() ? { serial_port: m.serial_port.trim() } : {}),
        ...(Number.isSafeInteger(m.port) ? { port: m.port } : {}),
        ...(typeof m.tag_folder === "string" && m.tag_folder.trim() ? { tag_folder: m.tag_folder.trim() } : {}),
        ...(Number.isSafeInteger(m.poll_interval_ms) ? { poll_interval_ms: m.poll_interval_ms } : {}),
      };
      let instantiated;
      try {
        instantiated = await api.instantiateDeviceTemplate(m.template, instance);
      } catch (templateError) {
        const message = String(templateError?.message || templateError);
        if (templateError?.status === 404) {
          throw new Error(`There is no device template called "${m.template}". Read the saved templates with list_device_templates and use one of those names.`);
        }
        throw new Error(`The device template could not be applied: ${message}`);
      }
      const templateSpec = instantiated?.spec;
      if (!templateSpec || !Array.isArray(templateSpec.mappings) || !templateSpec.mappings.length) {
        throw new Error(`The device template "${m.template}" produced no points, so there is nothing to connect.`);
      }
      // Identical to the reviewed path: create the missing cloud tags, declare
      // them to the runtime, wait for its catalog, then preview.
      const applied = await previewEdgeModbusClient(m.gateway_id, templateSpec, undefined);
      const templatePreview = applied.preview;
      // Fail closed before queueing anything: a preview missing its digest or
      // its revision is never confirmed, here or on the two-approval path.
      assertCompletePreview(templatePreview);
      await api.confirmEdgeConnector(m.gateway_id, {
        connector_id: templatePreview.connector_id,
        revision: templatePreview.revision,
        sha256: templatePreview.sha256,
        expected_revision: templatePreview.expected_revision,
      });
      const settled = await awaitConnectorInSync(
        m.gateway_id,
        templatePreview.connector_id,
        templatePreview.revision,
        templatePreview.sha256,
      );
      const where = templateSpec.config?.tcp
        ? `${templateSpec.config.tcp.host}:${templateSpec.config.tcp.port}`
        : templateSpec.config?.rtu?.port || "the serial bus";
      // BETA-53 ("Agregar dispositivo"): the wizard's whole point is that one
      // confirm leaves the device connected, its tags declared, AND history
      // turned on where the operator wants it — not three separate dialogs
      // (Device Templates, Connect, then Tags to flip the historian switch).
      // Reuses the exact tag paths this call just instantiated, so it never
      // depends on the tag-search index having caught up with a folder scan.
      // historian_enabled defaults to true; historian_mode defaults to 0
      // (on-change) when the caller does not say otherwise — the one setting
      // that adds no polling of its own, which is the right default for
      // Solis-class hardware that is already slow and easily overloaded
      // (docs/battex/beta/PROPOSAL-13-tag-setup.md §6.4).
      let historianNote = "";
      if (m.historian_enabled === false) {
        historianNote = "History left unchanged (historian_enabled:false).";
      } else {
        const historianTagPaths = [...new Set(
          templateSpec.mappings
            .map((mapping) => String(mapping.tag_path || "").trim())
            .filter(Boolean)
        )];
        const historianPolicy = {
          enabled: true,
          historian_mode: Number.isSafeInteger(m.historian_mode) ? m.historian_mode : 0,
          ...(Number.isSafeInteger(m.historian_interval_ms)
            ? { historian_interval_ms: m.historian_interval_ms }
            : {}),
        };
        // Chunked at the same 64-per-call width set_tags_historian enforces,
        // but internally: a template with more points than that must not
        // turn a working connection into a partial failure.
        const historianResults = [];
        for (let start = 0; start < historianTagPaths.length; start += 64) {
          // eslint-disable-next-line no-await-in-loop
          historianResults.push(
            ...(await applyHistorianToTagPaths(historianTagPaths.slice(start, start + 64), historianPolicy))
          );
        }
        const historianFailed = historianResults.filter((r) => !r.ok);
        const historianOk = historianResults.length - historianFailed.length;
        historianNote = historianFailed.length === 0
          ? `History enabled on all ${historianOk} tag(s).`
          : `History enabled on ${historianOk} of ${historianResults.length} tag(s); check ${historianFailed.map((r) => `"${r.path}"`).join(", ")} under Tags.`;
      }
      return [
        ...applied.notes,
        `Applied device template "${m.template}" as "${m.name}" on ${where}, device address ${m.unit_id}:`,
        `revision ${templatePreview.revision} with ${templateSpec.mappings.length} points was queued to the runtime.`,
        settled.summary,
        historianNote,
      ].join(" ");
    }
    case "set_tags_historian": {
      // Bulk historian enablement so the assistant can finish a monitoring
      // setup end-to-end. Applies the same per-tag PUT the UI uses.
      //
      // BETA-48 (control-seat-source#26): `folder_path` is an alternative
      // to `tag_paths` — the model names a folder ("default/solis") and the
      // dispatcher expands it to full tag paths itself, through the same
      // folder-tree-backed listing `list_tags_by_prefix` uses (BETA-41 found
      // no server-side "expand a folder to its tags" endpoint, so this stays
      // client-side, same as the read-only tool). Explicit `tag_paths` wins
      // if both are given.
      let tagPaths = Array.isArray(m.tag_paths)
        ? m.tag_paths.filter((p) => String(p || "").trim())
        : [];
      if (tagPaths.length === 0 && typeof m.folder_path === "string" && m.folder_path.trim()) {
        const folderPath = m.folder_path.trim();
        const matches = await listTagsByPrefix(folderPath, { limit: 1000 });
        if (matches.length === 0) {
          throw new Error(
            `No tags found under folder_path "${folderPath}". Check the path with list_tags or list_tags_by_prefix.`
          );
        }
        if (matches.length > 64) {
          throw new Error(
            `Folder "${folderPath}" has ${matches.length} tags — set_tags_historian accepts at most 64 at a time. ` +
            `Narrow the folder_path (e.g. a subfolder) or call it multiple times with explicit tag_paths.`
          );
        }
        tagPaths = matches.map((t) => t.fullPath);
      }
      if (tagPaths.length === 0 || tagPaths.length > 64) {
        throw new Error("Provide 1-64 tag_paths, or a folder_path resolving to 1-64 tags.");
      }
      const historianPolicy = {
        enabled: m.enabled !== false,
        ...(Number.isSafeInteger(m.historian_mode) ? { historian_mode: m.historian_mode } : {}),
        ...(Number.isSafeInteger(m.historian_interval_ms)
          ? { historian_interval_ms: m.historian_interval_ms }
          : {}),
      };
      const historianResults = await applyHistorianToTagPaths(tagPaths, historianPolicy);
      const historianFailed = historianResults.filter((r) => !r.ok);
      const historianOk = historianResults.length - historianFailed.length;
      // Every other kind returns a plain string summary — `dispatch()` wraps
      // whatever comes back as `{ok: true, summary}` and never inspects it,
      // so an object here (the old shape) silently reported success to the
      // model even when every tag failed to resolve. Fail loudly instead: a
      // caller (ungated path) that sees an exception reports `ok: false`
      // with the real reason, same as any other mutation.
      if (historianOk === 0) {
        // Zero matches almost always means a folder path was passed instead
        // of tag paths (see BETA-41 / control-seat-source#26): the model
        // called this with "default/solis" and the dispatcher reported
        // success even though nothing resolved. Name every unmatched input
        // and steer the model back to list_tags rather than letting it
        // retry the same folder path.
        const unresolved = historianFailed.map((r) => `"${r.path}"`).join(", ");
        throw new Error(
          `Historian enable matched 0 of ${historianFailed.length} tag(s): ${unresolved} did not resolve to a tag. ` +
          `Pass full tag paths from list_tags (e.g. "default/solis/power_kw") — a folder path is not accepted.`
        );
      }
      if (historianFailed.length > 0) {
        const failedList = historianFailed.map((r) => `"${r.path}" (${r.error})`).join(", ");
        return `Enabled historian on ${historianOk} of ${historianResults.length} tag(s). Failed: ${failedList}.`;
      }
      return `Enabled historian on ${historianOk} tag(s).`;
    }
    case "preview_edge_connector": {
      requireString(m, "gateway_id");
      requireObject(m, "connector_spec");
      if (m.connector_spec.kind === "modbus_tcp_server") {
        throw new Error("Signed edge Modbus TCP server revisions require the human Servers review and cannot be previewed or activated by AI.");
      }
      if (String(m.connector_spec.id || "").trim() && (!Number.isSafeInteger(m.expected_revision) || m.expected_revision < 0)) {
        throw new Error("Editing a connection requires its current expected_revision from the runtime inventory.");
      }
      const preview = await api.previewEdgeConnector(m.gateway_id, m.connector_spec, m.expected_revision);
      return connectorPreviewSummary(preview, m.gateway_id);
    }
    case "confirm_edge_connector": {
      requireString(m, "gateway_id");
      requireString(m, "connector_id");
      requirePositiveInteger(m, "revision");
      requireDigest(m, "sha256");
      if (!Number.isSafeInteger(m.expected_revision) || m.expected_revision < 0) {
        throw new Error("Connector confirmation requires the expected_revision returned by the reviewed preview.");
      }
      const history = await api.listEdgeConnectorHistory(m.gateway_id, m.connector_id);
      const reviewed = (Array.isArray(history?.revisions) ? history.revisions : []).find(
        (revision) => revision?.revision === m.revision && revision?.sha256 === m.sha256
      );
      if (!reviewed) {
        throw new Error("The exact reviewed connector revision is unavailable; AI confirmation fails closed.");
      }
      if (reviewed.kind === "modbus_tcp_server") {
        throw new Error("Signed edge Modbus TCP server confirmation is human-only on Servers.");
      }
      await api.confirmEdgeConnector(m.gateway_id, {
        connector_id: m.connector_id,
        revision: m.revision,
        sha256: m.sha256,
        expected_revision: m.expected_revision,
      });
      return "Queued the exact reviewed connector revision. It remains pending until the device reports the same applied digest.";
    }
    case "retry_edge_connector":
      requireString(m, "gateway_id");
      requireString(m, "connector_id");
      {
        const history = await api.listEdgeConnectorHistory(m.gateway_id, m.connector_id);
        if ((Array.isArray(history?.revisions) ? history.revisions : []).some((revision) => revision?.kind === "modbus_tcp_server")) {
          throw new Error("Signed edge Modbus TCP server retry is human-only on Servers.");
        }
      }
      await api.retryEdgeConnector(m.gateway_id, m.connector_id);
      return "Queued transport reconciliation for the exact desired connector revision.";
    case "preview_edge_connector_lifecycle": {
      requireString(m, "gateway_id");
      requireString(m, "connector_id");
      if (m.desired_state !== "running" && m.desired_state !== "stopped") {
        throw new Error('"desired_state" must be "running" or "stopped".');
      }
      const history = await api.listEdgeConnectorHistory(m.gateway_id, m.connector_id);
      if ((Array.isArray(history?.revisions) ? history.revisions : []).some((revision) => revision?.kind === "modbus_tcp_server")) {
        throw new Error("Signed edge Modbus TCP server lifecycle is human-only on Servers.");
      }
      const preview = await api.previewEdgeConnectorLifecycle(m.gateway_id, m.connector_id, m.desired_state);
      return connectorPreviewSummary(preview, m.gateway_id);
    }
    case "preview_edge_connector_rollback": {
      requireString(m, "gateway_id");
      requireString(m, "connector_id");
      requirePositiveInteger(m, "revision");
      const history = await api.listEdgeConnectorHistory(m.gateway_id, m.connector_id);
      const target = (Array.isArray(history?.revisions) ? history.revisions : []).find((revision) => revision?.revision === m.revision);
      if (!target) throw new Error("The rollback connector revision is unavailable; AI review fails closed.");
      if (target.kind === "modbus_tcp_server") {
        throw new Error("Signed edge Modbus TCP server rollback is human-only on Servers.");
      }
      const preview = await api.previewEdgeConnectorRollback(m.gateway_id, m.connector_id, m.revision);
      return connectorPreviewSummary(preview, m.gateway_id);
    }

    default:
      throw new Error(`Unknown mutation kind: "${m.kind}".`);
  }
}

function requireString(m, key) {
  if (typeof m[key] !== "string" || !m[key]) {
    throw new Error(`Mutation "${m.kind}" requires "${key}" (string).`);
  }
}
function requireArray(m, key) {
  if (!Array.isArray(m[key]) || m[key].length === 0) {
    throw new Error(`Mutation "${m.kind}" requires "${key}" (non-empty array).`);
  }
}

function requireObject(m, key) {
  if (!m[key] || typeof m[key] !== "object" || Array.isArray(m[key])) {
    throw new Error(`Mutation "${m.kind}" requires "${key}" (object).`);
  }
}

function requirePositiveInteger(m, key) {
  if (!Number.isInteger(m[key]) || m[key] <= 0) {
    throw new Error(`Mutation "${m.kind}" requires "${key}" (positive integer).`);
  }
}

function requireDigest(m, key) {
  requireString(m, key);
  if (!/^[a-f0-9]{64}$/i.test(m[key])) {
    throw new Error(`Mutation "${m.kind}" requires "${key}" as an exact SHA-256 digest.`);
  }
}

// Modbus register formats accepted in points_csv and in points[].
// Operators paste the datasheet spelling (UINT16, FLOAT32) as often as our
// short form (U16, F32), and a model copies whichever the table used.
const MODBUS_CSV_VALUE_TYPES = {
  U16: "uint16", UINT16: "uint16", UNSIGNED16: "uint16", WORD: "uint16",
  S16: "int16", I16: "int16", INT16: "int16", SIGNED16: "int16", SHORT: "int16",
  U32: "uint32", UINT32: "uint32", UNSIGNED32: "uint32", DWORD: "uint32",
  S32: "int32", I32: "int32", INT32: "int32", SIGNED32: "int32",
  U64: "uint64", UINT64: "uint64", S64: "int64", I64: "int64", INT64: "int64",
  F32: "float32", FLOAT32: "float32", FLOAT: "float32", REAL: "float32",
  F64: "float64", FLOAT64: "float64", DOUBLE: "float64",
};
const MODBUS_BOOL_TYPES = new Set(["BOOL", "BOOLEAN", "BIT", "COIL", "DISCRETE"]);
const MODBUS_ACCEPTED_TYPES =
  "U16/UINT16, S16/INT16, U32/UINT32, S32/INT32, U64/UINT64, S64/INT64, F32/FLOAT32, F64/FLOAT64, BOOL/BOOLEAN (case-insensitive)";
// Write function codes an operator's table names (FC6 "write single register",
// FC5 "write single coil", FC16/FC22/FC23) map onto the readable function the
// signed spec carries plus a writable point: the runtime picks the write code.
const MODBUS_WRITE_FUNCTION_CODES = { 5: 1, 15: 1, 6: 3, 16: 3, 22: 3, 23: 3 };
const MODBUS_ACCEPTED_FUNCTIONS =
  "fc1/fc2/fc3/fc4 to read, fc5/fc6/fc15/fc16 for a writable point";

const modbusTypeKey = (value) => String(value || "").replace(/[\s_-]+/g, "").toUpperCase();

// Every platform tag path is rooted at the project namespace, and the control
// plane rejects a mapping path that is not. A model that writes "solis/SOC"
// means "default/solis/SOC" — repair it here rather than failing the preview.
function edgeTagFullPath(path) {
  const trimmed = String(path || "").trim().replace(/^\/+|\/+$/g, "");
  if (!trimmed) return "";
  return trimmed === "default" || trimmed.startsWith("default/") ? trimmed : `default/${trimmed}`;
}

const modbusTagFolderPrefix = (tagFolder) => {
  const folder = String(tagFolder || "").trim().replace(/^\/+|\/+$/g, "");
  return folder ? `${folder}/` : "";
};

// A datasheet table pasted verbatim usually keeps its header row.
const isModbusCsvHeaderRow = (cells) =>
  !Number.isSafeInteger(Number(cells[1])) &&
  /^(name|tag|tag_?name|tag_?path|point)$/i.test(String(cells[0] || "").trim());

// Trailing points_csv cells are free-form: the function code, the writability
// marker, the poll interval, and the operator's notes arrive in any order and
// in whatever spelling the source table used. Anything unrecognized is a note.
function modbusCsvRowOptions(tokens, row) {
  const options = {};
  for (const raw of tokens) {
    const token = String(raw || "").trim();
    if (!token) continue;
    const fc = /^fc\s*=?\s*(\d{1,2})$/i.exec(token);
    const poll = /^(?:poll(?:_?interval(?:_?ms)?)?\s*=\s*(\d+)|(\d+)\s*ms)$/i.exec(token);
    if (fc || /^\d{1,2}$/.test(token)) {
      const code = Number(fc ? fc[1] : token);
      if (code >= 1 && code <= 4) {
        options.functionCode = code;
        continue;
      }
      if (MODBUS_WRITE_FUNCTION_CODES[code]) {
        options.functionCode = MODBUS_WRITE_FUNCTION_CODES[code];
        options.writable = true;
        continue;
      }
      if (fc) {
        throw new Error(`points_csv row "${row}": unsupported function code ${token}. Accepted: ${MODBUS_ACCEPTED_FUNCTIONS}.`);
      }
    }
    if (poll) {
      options.pollIntervalMs = Number(poll[1] ?? poll[2]);
      continue;
    }
    if (/^(rw|wr|w|write|writable|read_?write)$/i.test(token)) {
      options.writable = true;
      continue;
    }
    if (/^(ro|r|read|readonly|read_?only|observe)$/i.test(token)) {
      options.writable = false;
      continue;
    }
    if (/^\d+$/.test(token) && Number(token) >= 50) {
      options.pollIntervalMs = Number(token);
      continue;
    }
  }
  return options;
}

// modbusPointsFromCsv expands compact "name,addr,type[,div[,fc[,rw[,pollMs]]]]"
// rows (the shape of a device datasheet table) into full wizard point objects.
// div is the raw divisor: div=1 keeps an Integer tag; div>1 becomes a Number
// tag with scale 1/div (the builder requires a non-identity scale when an
// integer register feeds a Number tag). Read-only points default to fc2 (bool)
// or fc4 (numeric); an `rw` marker makes the point writable, which defaults to
// a coil (fc1) or holding register (fc3) — FC3 read, FC6 write on the wire —
// and creates the cloud tag as writable.
function modbusPointsFromCsv(csv, tagFolder, deviceSettings) {
  const folder = modbusTagFolderPrefix(tagFolder);
  const defaultPoll = Number.isSafeInteger(deviceSettings.pollIntervalMs)
    ? deviceSettings.pollIntervalMs
    : 1000;
  const rows = String(csv)
    .split(/[\r\n;]+/)
    .map((row) => row.trim())
    .filter(Boolean);
  const points = [];
  rows.forEach((row, index) => {
    const cells = row.split(",").map((cell) => String(cell || "").trim());
    if (index === 0 && isModbusCsvHeaderRow(cells)) return;
    const [name, addrRaw, typeRaw, divRaw] = cells;
    const address = Number(addrRaw);
    if (!name || !Number.isSafeInteger(address)) {
      throw new Error(`points_csv row is invalid: "${row}" (need name,addr,type[,div[,fc[,rw[,pollMs]]]])`);
    }
    const divIsNumeric = String(divRaw || "").trim() === "" || Number.isFinite(Number(divRaw));
    const options = modbusCsvRowOptions(
      divIsNumeric ? cells.slice(4) : cells.slice(3),
      row
    );
    const type = modbusTypeKey(typeRaw) || "U16";
    const isBool = MODBUS_BOOL_TYPES.has(type);
    const valueType = MODBUS_CSV_VALUE_TYPES[type];
    if (!isBool && !valueType) {
      throw new Error(
        `points_csv row "${row}": unsupported type ${String(typeRaw).trim()}. ` +
        `Accepted: ${MODBUS_ACCEPTED_TYPES}. Cell 3 is the type — the row is name,addr,type[,div[,fc[,rw[,pollMs]]]], ` +
        "so reorder the operator's columns instead of pasting their order."
      );
    }
    const div = divIsNumeric ? Number(divRaw || 1) || 1 : 1;
    const writable = options.writable === true;
    const functionCode = options.functionCode || (writable ? (isBool ? 1 : 3) : isBool ? 2 : 4);
    const scaled = !isBool && div !== 1;
    // A float register carries its own engineering value, so it needs a Number
    // tag even unscaled; the builder rejects a float format on an Integer tag.
    const isFloat = valueType === "float32" || valueType === "float64";
    points.push({
      tagPath: edgeTagFullPath(folder + name),
      tagType: isBool ? "boolean" : scaled || isFloat ? "number" : "integer",
      direction: writable ? "output" : "input",
      settings: {
        function: functionCode,
        address,
        addressBase: "zero_based",
        ...(isBool ? {} : { valueType }),
        ...(scaled ? { scale: 1 / div } : {}),
        pollIntervalMs: Number.isSafeInteger(options.pollIntervalMs) ? options.pollIntervalMs : defaultPoll,
      },
    });
  });
  return points;
}

// The explicit points[] form gets the same three repairs the CSV form gets:
// project-rooted tag paths, a writability marker the builder understands, and
// the device-wide poll interval when the point does not carry its own.
function modbusPointsFromArray(points, tagFolder, deviceSettings) {
  const folder = modbusTagFolderPrefix(tagFolder);
  return points.map((point) => {
    const source = point && typeof point === "object" ? point : {};
    const settings = source.settings && typeof source.settings === "object" ? source.settings : {};
    const rawPath = String(source.tagPath || source.tag_path || "").trim();
    const writable = source.direction === "output" || source.writable === true || source.rw === true;
    const pollIntervalMs = [settings.pollIntervalMs, source.pollIntervalMs, deviceSettings.pollIntervalMs]
      .find((value) => Number.isSafeInteger(value));
    return {
      ...source,
      tagPath: edgeTagFullPath(rawPath.includes("/") ? rawPath : folder + rawPath),
      direction: writable ? "output" : "input",
      settings: {
        ...settings,
        ...(Number.isSafeInteger(pollIntervalMs) ? { pollIntervalMs } : {}),
      },
    };
  });
}

// Bounded wait for the runtime to confirm a tags.deploy. The runtime reports
// its catalog on the heartbeat (30-60 s over 4G), so the AI flow has to wait
// like the wizard does — but never forever, and never at all when the runtime
// is offline. Tests replace `wait`/`now` to run the same bounded loop without
// spending real time.
export const EDGE_TAG_PREREQUISITE = {
  budgetMs: 90000,
  firstDelayMs: 1500,
  maxDelayMs: 8000,
  wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now: () => Date.now(),
};

// The control plane rejects a preview whose mapping tags the RUNTIME has not
// reported. These are its exact refusals — every one of them clears once the
// declaration lands, so they are retryable rather than fatal.
const RUNTIME_TAG_PREREQUISITE =
  /absent from the runtime|was not proven by the runtime|fresh current runtime tag catalog|runtime tag catalog is unavailable/i;
const CONNECTOR_REVISION_CONFLICT = /changed since you opened/i;

const isRuntimeTagPrerequisite = (message) => RUNTIME_TAG_PREREQUISITE.test(message);
const isConnectorRevisionConflict = (error, message) =>
  CONNECTOR_REVISION_CONFLICT.test(message) || error?.status === 409;

// Cloud tag declarations behind one Modbus mapping set, derived from the
// signed spec so the tag's type and writability can never drift from the
// mapping that binds it.
function modbusMappingTagRequirements(spec) {
  return (Array.isArray(spec?.mappings) ? spec.mappings : []).map((mapping) => {
    const fullPath = String(mapping?.tag_path || "");
    const segments = fullPath.split("/");
    const writable = mapping?.flow === "command" || mapping?.flow === "bidirectional";
    return {
      fullPath,
      name: segments[segments.length - 1],
      // POST /api/tags takes the path under the project root; the runtime and
      // the mapping both speak the full "default/..." path.
      path: segments.slice(1).join("/"),
      type: String(mapping?.data_type || "float64"),
      writable,
      direction: writable ? "output" : "input",
    };
  });
}

// Which of those the runtime has NOT yet proven. A failure to inspect is not
// evidence of absence: the preview itself is the authority, so an unavailable
// catalog endpoint leaves the prerequisite untouched.
async function outstandingRuntimeTags(gatewayID, wanted) {
  const payload = await api.inspectEdgeTagCatalog(gatewayID, wanted.map((tag) => tag.fullPath));
  return wanted
    .map((tag) => ({ tag, eligibility: edgeConnectorTagEligibility(payload, tag.fullPath, tag.direction) }))
    .filter((candidate) => !candidate.eligibility?.eligible);
}

// The prerequisite the human wizard performs silently (see EdgeConnectorReview
// .preparePendingConnector): create the missing cloud tags, declare them to the
// runtime, then wait for the runtime's own catalog to confirm them. Without it
// the AI preview stalls at 422 "tag absent from the runtime" (issue #5).
async function ensureRuntimeMappingTags(gatewayID, spec, deadline, { force = false } = {}) {
  const wanted = modbusMappingTagRequirements(spec);
  if (!wanted.length) return [];
  let outstanding = [];
  try {
    outstanding = await outstandingRuntimeTags(gatewayID, wanted);
  } catch {
    if (!force) return [];
    outstanding = wanted.map((tag) => ({ tag, eligibility: null }));
  }
  if (!outstanding.length) return [];

  // An offline runtime can never confirm the declaration, so say that once
  // instead of burning the whole retry budget on it.
  const inventory = await api.listEdgeConnectors(gatewayID).catch(() => null);
  const connection = inventory?.runtime?.connection;
  if (connection && connection !== "online") {
    throw new Error(
      "The runtime is offline, so it cannot confirm the tags this connection needs. Bring the runtime online and propose this connection again."
    );
  }

  const created = [];
  for (const { tag } of outstanding) {
    // Ordered creation, mirroring the wizard: a create that succeeded on the
    // server but lost its response must not become a duplicate on retry.
    // eslint-disable-next-line no-await-in-loop
    try {
      await api.createOrdinaryTag({
        name: tag.name,
        path: tag.path,
        type: tag.type,
        writable: tag.writable,
        description: `Created for ${spec.name}`,
      });
      created.push(tag.fullPath);
    } catch (createError) {
      const existingID = await api.resolveTagPathToId?.(tag.fullPath).catch(() => null);
      const existing = existingID ? await api.fetchTagById?.(existingID).catch(() => null) : null;
      const sameTag = existing && existing.fullPath === tag.fullPath &&
        existing.type === tag.type && Boolean(existing.writable) === Boolean(tag.writable);
      if (!sameTag) {
        throw new Error(
          `Could not create the cloud tag ${tag.fullPath} (${tag.type}${tag.writable ? ", writable" : ""}): ${String(createError?.message || createError)}`
        );
      }
    }
  }
  await api.deployEdgeTags(gatewayID, outstanding.map(({ tag }) => ({
    path: tag.fullPath,
    name: tag.name,
    type: tag.type,
    writable: tag.writable,
  })));

  let delay = EDGE_TAG_PREREQUISITE.firstDelayMs;
  let pending = outstanding;
  while (EDGE_TAG_PREREQUISITE.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await EDGE_TAG_PREREQUISITE.wait(delay);
    delay = Math.min(Math.round(delay * 1.5), EDGE_TAG_PREREQUISITE.maxDelayMs);
    // eslint-disable-next-line no-await-in-loop
    pending = await outstandingRuntimeTags(gatewayID, wanted).catch(() => pending);
    if (!pending.length) return created;
  }
  const blocked = pending[0];
  throw new Error(
    `The runtime has not confirmed ${pending.length} tag${pending.length === 1 ? "" : "s"} yet` +
    ` (for example ${blocked.tag.fullPath}: ${blocked.eligibility?.message || "not reported"}).` +
    " The declaration is queued — wait for the runtime to report its tag catalog and propose this connection again."
  );
}

// The connection's live desired revision, which is what `expected_revision`
// must equal. Returns null when the inventory does not carry it.
async function currentConnectorRevision(gatewayID, connectorID) {
  if (!connectorID) return null;
  const inventory = await api.listEdgeConnectors(gatewayID).catch(() => null);
  const connectors = Array.isArray(inventory?.connectors) ? inventory.connectors : [];
  const match = connectors.find((connector) => connector?.connector_id === connectorID);
  return Number.isSafeInteger(match?.desired_revision) ? match.desired_revision : null;
}

/**
 * Preview one AI-authored Modbus client spec, absorbing the two failures the
 * model cannot recover from on its own (issues #5 and #12):
 *
 *  - the runtime has not been told about the mapping tags yet, and
 *  - the connection moved on since the approval card was built.
 *
 * The revision refresh is safe here because a preview changes nothing on the
 * device: it allocates a revision the human still confirms by digest, and that
 * confirmation keeps the exact `expected_revision` the preview returned.
 */
async function previewEdgeModbusClient(gatewayID, spec, expectedRevision) {
  const deadline = EDGE_TAG_PREREQUISITE.now() + EDGE_TAG_PREREQUISITE.budgetMs;
  const notes = [];
  const created = await ensureRuntimeMappingTags(gatewayID, spec, deadline);
  if (created.length) {
    notes.push(`Created and declared ${created.length} tag${created.length === 1 ? "" : "s"} on the runtime first.`);
  }
  let expected = expectedRevision;
  let refreshedRevision = false;
  let repairedTags = false;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return { preview: await api.previewEdgeConnector(gatewayID, spec, expected), notes };
    } catch (error) {
      const message = String(error?.message || error);
      if (isConnectorRevisionConflict(error, message)) {
        const current = await currentConnectorRevision(gatewayID, spec.id);
        if (refreshedRevision || current == null || current === expected) {
          throw new Error(
            current == null
              ? "This connection changed since the proposal was built, and its current revision could not be read from the runtime inventory. Read the connections again and propose the preview with the connection's current expected_revision."
              : `This connection changed again while the preview was being prepared. Its current expected_revision is ${current} — propose the preview again with expected_revision=${current}.`
          );
        }
        notes.push(`The connection had moved to revision ${current} since the proposal was built, so this preview was rebuilt on it.`);
        expected = current;
        refreshedRevision = true;
        continue;
      }
      if (isRuntimeTagPrerequisite(message) && !repairedTags) {
        repairedTags = true;
        const repaired = await ensureRuntimeMappingTags(gatewayID, spec, deadline, { force: true });
        if (repaired.length) {
          notes.push(`Created and declared ${repaired.length} tag${repaired.length === 1 ? "" : "s"} on the runtime first.`);
        }
        continue;
      }
      throw error;
    }
  }
  throw new Error("The connection kept changing while this preview was prepared. Read the connections again and propose it with the current expected_revision.");
}

// Bounded wait for the runtime to report a confirmed revision as applied. Only
// apply_device_template uses it: it is the one mutation that queues a revision
// itself, so it is the one that owes the operator the outcome rather than a
// "queued, go and look". Tests replace `wait`/`now` to run the loop without
// spending real time, exactly as they do for EDGE_TAG_PREREQUISITE.
export const EDGE_APPLY_SYNC = {
  budgetMs: 120000,
  firstDelayMs: 2000,
  maxDelayMs: 8000,
  wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now: () => Date.now(),
};

// A preview is only usable if it is digest-bound: without the exact revision
// and sha256 there is nothing to confirm, and confirming a guess is the one
// thing this flow must never do.
function assertCompletePreview(preview) {
  if (!preview?.connector_id || !Number.isInteger(preview?.revision) ||
      !Number.isSafeInteger(preview?.expected_revision) || preview.expected_revision < 0 ||
      !/^[a-f0-9]{64}$/i.test(preview?.sha256 || "")) {
    throw new Error("The control plane did not return a complete digest-bound connector preview.");
  }
  return preview;
}

// Watch one connector until the device confirms the exact revision that was
// queued. Never throws on a timeout: the revision IS queued, and reporting
// "still waiting" truthfully beats reporting a failure that did not happen.
async function awaitConnectorInSync(gatewayID, connectorID, revision, sha256) {
  const deadline = EDGE_APPLY_SYNC.now() + EDGE_APPLY_SYNC.budgetMs;
  let delay = EDGE_APPLY_SYNC.firstDelayMs;
  let last = null;
  while (EDGE_APPLY_SYNC.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await EDGE_APPLY_SYNC.wait(delay);
    delay = Math.min(Math.round(delay * 1.5), EDGE_APPLY_SYNC.maxDelayMs);
    // eslint-disable-next-line no-await-in-loop
    const inventory = await api.listEdgeConnectors(gatewayID).catch(() => null);
    const connector = (Array.isArray(inventory?.connectors) ? inventory.connectors : [])
      .find((row) => row?.connector_id === connectorID);
    if (!connector) continue;
    last = connector;
    const applied = connector.applied || {};
    if (connector.sync_state === "in_sync" && applied.applied_revision === revision &&
        String(applied.applied_sha256 || "") === String(sha256)) {
      const good = Number.isSafeInteger(applied.point_good) ? applied.point_good : null;
      const degraded = Number.isSafeInteger(applied.point_degraded) ? applied.point_degraded : null;
      return {
        state: "in_sync",
        connector,
        summary: `The runtime confirmed it and the connection is in sync${
          good == null ? "" : ` — ${good} point${good === 1 ? "" : "s"} reporting${degraded ? `, ${degraded} still settling` : ""}`
        }.`,
      };
    }
    if (connector.sync_state === "rejected") {
      return {
        state: "rejected",
        connector,
        summary: `The runtime rejected revision ${revision}${applied.error ? `: ${applied.error}` : "."} The connection is listed and can be retried once the cause is fixed.`,
      };
    }
  }
  return {
    state: last?.sync_state || "pending",
    connector: last,
    summary: `The runtime has not confirmed it yet — the revision is queued and the connection shows as pending. Read the connections again in a minute to see it in sync.`,
  };
}

function connectorPreviewSummary(preview, gatewayID) {
  assertCompletePreview(preview);
  return [
    `Prepared revision ${preview.revision} of "${preview.name}" for ${preview.target}. No device configuration has changed.`,
    `Exact confirmation fields: gateway_id=${gatewayID}; connector_id=${preview.connector_id}; revision=${preview.revision}; sha256=${preview.sha256}; expected_revision=${preview.expected_revision}.`,
    "Ask for a second confirmation using those exact fields before queueing it to the edge runtime.",
  ].join(" ");
}

// Alarms canonical id is `alarm_id`. Models often pass `id` instead — accept
// either, fail clearly otherwise.
function pickAlarmId(m) {
  const id = m.alarm_id || m.id;
  if (typeof id !== "string" || !id) {
    throw new Error(
      `Mutation "${m.kind}" requires "alarm_id" (string). The list_alarms tool returns this as alarm_id.`
    );
  }
  return id;
}

const VALID_ROLES = new Set(["admin", "user", "viewer"]);
function requireRole(m) {
  requireString(m, "role");
  if (!VALID_ROLES.has(m.role)) {
    throw new Error(
      `Mutation "${m.kind}" got role "${m.role}" — must be one of: admin, user, viewer.`
    );
  }
}

/**
 * Find the topmost visible modal, click its submit button, and watch for
 * either modal-close (success) or a toast (success or error). Returns a
 * human-readable summary either way.
 */
async function submitOpenForm() {
  if (typeof document === "undefined") {
    throw new Error("submit_open_form is only available in the browser.");
  }
  const modal = pickTopModal();
  if (!modal) {
    throw new Error("No form is open to submit.");
  }
  if (
    modal.matches?.('[data-gw-ai-protected-submit="reviewed-opcua-confirm"]') ||
    modal.closest?.('[data-gw-ai-protected-submit="reviewed-opcua-confirm"]')
  ) {
    throw new Error(
      "Generic form submission cannot confirm a reviewed OPC UA desired revision. Use the OPC UA review dialog's explicit human confirmation."
    );
  }
  if (
    modal.matches?.('[data-gw-ai-form-control="reviewed-plc-deployment"]') ||
    modal.closest?.('[data-gw-ai-form-control="reviewed-plc-deployment"]')
  ) {
    throw new Error(
      "Generic form submission cannot execute reviewed PLC deployment signing or fleet rollout controls. The operator must use the exact review dialog directly."
    );
  }
  const submitBtn =
    modal.querySelector(".gwModalFooter button[type='submit']") ||
    modal.querySelector("button[type='submit']") ||
    modal.querySelector(".gwModalFooter .gwPrimaryBtn");
  if (!submitBtn) {
    throw new Error("Couldn't find a submit button on the open form.");
  }
  if (submitBtn.disabled) {
    throw new Error("Submit button is disabled — required fields may be empty.");
  }
  const before = snapshotToasts();
  submitBtn.click();
  // Poll up to ~15s for either: modal removed (success) or new toast.
  // 15s accommodates slower flows like creating a DB connection (which
  // often pings the server before the modal closes); the prior 3.5s
  // window false-failed those.
  const deadline = Date.now() + 15000;
  while (Date.now() < deadline) {
    await wait(120);
    if (!document.contains(modal)) {
      const newToasts = collectNewToasts(before);
      if (newToasts.length > 0) {
        const last = newToasts[newToasts.length - 1];
        if (last.type === "error") throw new Error(last.text || "Submission failed.");
        return last.text || "Saved.";
      }
      return "Saved.";
    }
    const newToasts = collectNewToasts(before);
    const error = newToasts.find((t) => t.type === "error");
    if (error) throw new Error(error.text || "Submission failed.");
  }
  throw new Error(
    "Submission timed out — the form is still open. Check for inline errors and try again."
  );
}

function pickTopModal() {
  const overlays = Array.from(document.querySelectorAll(".gwOverlay"));
  if (overlays.length === 0) {
    const lone = document.querySelector(".gwModal");
    return lone || null;
  }
  // Last-rendered overlay is on top.
  const top = overlays[overlays.length - 1];
  return top.querySelector(".gwModal") || top;
}

function snapshotToasts() {
  return Array.from(document.querySelectorAll(".toastItem")).map((el) => ({
    el,
    text: toastText(el),
  }));
}

function toastText(el) {
  const title = el.querySelector(".toastTitle")?.textContent?.trim() || "";
  const message = el.querySelector(".toastMessage")?.textContent?.trim() || "";
  return [title, message].filter(Boolean).join(" — ");
}

function toastType(el) {
  if (el.classList.contains("toast-error")) return "error";
  if (el.classList.contains("toast-success")) return "success";
  return "info";
}

function collectNewToasts(beforeSnap) {
  const beforeEls = new Set(beforeSnap.map((s) => s.el));
  return Array.from(document.querySelectorAll(".toastItem"))
    .filter((el) => !beforeEls.has(el))
    .map((el) => ({ type: toastType(el), text: toastText(el) }));
}

function wait(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
